import classNames from "classnames";
import React, { useState, DragEvent, ChangeEvent } from "react";
import { formatBytes, getNestedError } from "../../../utils/helpers";
import { CloseSvg } from "../../../icons";
import { FieldError, RegisterOptions, useController } from "react-hook-form";

type IFileDropZonProps = {
  label?: string;
  onSelectOrDrop?: (files: FileList) => void;
  onClear?: () => void;
  accept?: string;
  name?: string;
  rules?: Omit<
    RegisterOptions,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  resetFile?: boolean;
  onChange?: (files: File[] | null) => void;
  multiple?: boolean;
  maxFiles?: number;
};
const MAX_SIZE_MB = 20;
const FileDropZone = ({
  label,
  onSelectOrDrop,
  onClear,
  accept,
  name,
  rules,
  resetFile,
  onChange,
  multiple = false,
  maxFiles = 10,
}: IFileDropZonProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const {
    field,
    formState: { errors },
  } = useController({ name: name || "", rules });
  const { ref, value, onChange: fieldOnChange, ...otherFieldValue } = field;

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const toFileList = (files: File[]): FileList => {
    const dataTransfer = new DataTransfer();
    files.forEach((f) => dataTransfer.items.add(f));
    return dataTransfer.files;
  };

  const processFiles = (files: File[]) => {
    const valid: File[] = [];
    for (const file of files) {
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        alert(`File ${file.name} is too large. Max allowed size is ${MAX_SIZE_MB}MB.`);
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) return;

    const total = previewFiles.length + valid.length;
    if (multiple && total > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed. You can only add ${maxFiles - previewFiles.length} more files.`);
      return;
    }

    const newFiles = multiple ? [...previewFiles, ...valid] : [valid[0]];
    setPreviewFiles(newFiles);

    if (multiple) {
      fieldOnChange(newFiles);
      onChange?.(newFiles);
    } else {
      fieldOnChange(newFiles[0] ?? null);
      onChange?.(newFiles.length ? [newFiles[0]] : null);
    }

    onSelectOrDrop?.(toFileList(newFiles));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) processFiles(dropped);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    if (selected.length) processFiles(selected);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearOne = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    const updated = previewFiles.filter((_, i) => i !== index);
    setPreviewFiles(updated);
    if (multiple) {
      fieldOnChange(updated);
      onChange?.(updated);
    } else {
      fieldOnChange(null);
      onChange?.(null);
    }
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewFiles([]);
    fieldOnChange(multiple ? [] : null);
    onChange?.(multiple ? [] : null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClear?.();
  };

  React.useEffect(() => {
    if (resetFile) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      onClear?.();
      setPreviewFiles([]);
    }
  }, [resetFile, onClear]);

  const renderPreview = (file: File, index: number) => {
    const isImage = file.type.startsWith("image/");
    const url = URL.createObjectURL(file);
    return (
      <div key={`${file.name}-${index}`} className="relative">
        <div className="w-24 h-24 border border-gray-300 rounded-lg overflow-hidden bg-gray-100">
          {isImage ? (
            <img
              src={url}
              alt={file.name}
              className="w-full h-full object-cover"
              onLoad={() => URL.revokeObjectURL(url)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-xs text-gray-500 p-1 break-all text-center">
                {file.name.split(".").pop()?.toUpperCase()}
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => handleClearOne(e, index)}
          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
        >
          ×
        </button>
        <div className="text-xs text-gray-600 mt-1 truncate w-24" title={file.name}>
          {file.name}
        </div>
        <div className="text-xs text-gray-400">{formatBytes(file.size)}</div>
      </div>
    );
  };

  return (
    <div>
      {label && (
        <label className="text-sm leading-[18.03px] font-normal text-[#666672]">{label}</label>
      )}
      <div
        className={classNames(
          "w-full h-28 border-[0.86px] border-dashed rounded flex flex-col justify-center items-center text-xl text-gray-600 transition-colors cursor-pointer",
          label ? "mt-2.5" : "",
          isDragging ? "bg-secondary/5 border-secondary" : "bg-gray-[#D9D9D933] border-gray-400"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <p className="text-sm leading-[18.03px] font-normal text-[#666672]">
          {isDragging ? (
            "Release to drop files"
          ) : previewFiles.length > 0 ? (
            <div className="flex items-center gap-5">
              <div>
                {previewFiles.length} file{previewFiles.length > 1 ? "s" : ""} selected
              </div>
              <div onClick={handleClearAll}>
                <CloseSvg className="text-[#1D1B20]" />
              </div>
            </div>
          ) : (
            `Drag Files or Click to Browse${multiple ? " (Multiple files allowed)" : ""}`
          )}
        </p>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          multiple={multiple}
          onChange={handleFileInputChange}
          accept={accept}
          {...otherFieldValue}
        />
      </div>
       <div className="text-sm leading-[18.03px] font-normal text-[#666672] mt-2" style={{
        display:'flex',
        justifyContent:'space-between'
      }}>
        <span>Accepts:  jpeg, jpg, png, gif, webp</span>
        <span>Max File: {maxFiles}</span>
      </div>
      

      {previewFiles.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Selected Files ({previewFiles.length})</span>
            {multiple && previewFiles.length > 1 && (
              <button type="button" onClick={handleClearAll} className="text-sm text-red-500 hover:text-red-700">
                Clear All
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-3">{previewFiles.map(renderPreview)}</div>
        </div>
      )}

      {errors && name && (
        <span className="text-red-500 text-sm">{(getNestedError(errors, name) as FieldError)?.message}</span>
      )}
    </div>
  );
};

export default FileDropZone;
